import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Dashboard.css";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import {
  getADPerTimeInfoList,
  getADPerDayInfoList,
  getADPerWeekInfoList,
  getSumADInfo,
  getSumADInfoFrom,
  getGuestPerDayInfoList,
} from "../../api/project";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const Dashboard = () => {
  const navigate = useNavigate();

  // 状態変数
  const [aDPerTimeInfoList, setADPerTimeInfoList] = useState([]);
  const [aDPerWeekInfoList, setADPerWeekInfoList] = useState([]);
  const [aDPerDayInfoList, setADPerDayInfoList] = useState([]);
  const [sumADInfoList, setSumADInfoList] = useState([]);
  const [sumADInfoFromList, setSumADInfoFromList] = useState([]);
  const [guestPerDayInfoList, setGuestPerDayInfoList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("jankatsu");
  const [isLoading, setIsLoading] = useState(false);

  const JM_COLOR = {
    max: "#1b5e20",
    avg: "#1565c0",
    min: "#b71c1c",
  };

  const OSHICAL_COLOR = {
    max: "#00c853",
    avg: "#1de9b6",
    min: "#ff5252",
  };

  const JM_MOJI = {
    max: "(雀)大",
    avg: "(雀)均",
    min: "(雀)小",
  };

  const OSHICAL_MOJI = {
    max: "(推)大",
    avg: "(推)均",
    min: "(推)小",
  };

  const showRefLabel = activeTab !== "jankatsu";

  // 期間選択用の状態
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    // デフォルト過去１週間
    date.setDate(date.getDate() - 6);
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    // デフォルト将来１週間
    //date.setDate(date.getDate() + 7);
    return date;
  });

  // データ生成関数
  const generateMockData = useCallback(
    (startDate, endDate) => {
      const daysDiff = Math.ceil(
        (endDate - startDate + 1) / (1000 * 60 * 60 * 24),
      );

      // 時間分布データ（実際のデータを使用）
      const timeDistribution =
        aDPerTimeInfoList.length > 0
          ? aDPerTimeInfoList.map((item) => ({
              time: item.time_from,
              impressions_jm: item.impressions_jm || 0,
              impressions_oshical: item.impressions_oshical || 0,
            }))
          : [
              // データがない場合のデフォルト
              { time: "00:00", impressions_jm: 0, impressions_oshical: 0 },
              { time: "12:00", impressions_jm: 100, impressions_oshical: 50 },
              { time: "18:00", impressions_jm: 150, impressions_oshical: 75 },
            ];

      // 日々インプレッション分布データ（実際のデータを使用）
      const dayDistribution =
        aDPerDayInfoList.length > 0
          ? aDPerDayInfoList.map((item) => ({
              target_date: item.target_date,
              impressions_jm: item.impressions_jm || 0,
              impressions_oshical: item.impressions_oshical || 0,
            }))
          : [
              // データがない場合のデフォルト
              {
                target_date: "2026-01-16",
                impressions_jm: 0,
                impressions_oshical: 0,
              },
              {
                target_date: "2026-01-17",
                impressions_jm: 0,
                impressions_oshical: 0,
              },
              {
                target_date: "2026-01-18",
                impressions_jm: 0,
                impressions_oshical: 0,
              },
            ];
      // 日々分布データ（実際のデータを使用）
      const dayGuestDistribution =
        guestPerDayInfoList.length > 0
          ? guestPerDayInfoList.map((item) => ({
              target_date: item.target_date,
              guest_cnt: item.guest_cnt || 0,
            }))
          : [
              // データがない場合のデフォルト
              {
                target_date: "2026-01-16",
                guest_cnt: 0,
              },
            ];

      // 広告集計情報
      const sumADInfo =
        sumADInfoList.length > 0
          ? sumADInfoList[0]
          : {
              jm_ad_cnt: 0,
              oshical_ad_cnt: 0,
              user_cnt: 0,
              jm_home_init_cnt: 0,
              oshical_home_init_cnt: 0,
              ad_sum: 0,
              jm_ad_click: 0,
              oshical_ad_click: 0,
            };

      // 広告集計情報
      const sumADInfoFrom =
        sumADInfoFromList.length > 0
          ? sumADInfoFromList[0]
          : {
              jm_ad_cnt: 0,
              oshical_ad_cnt: 0,
              user_cnt: 0,
              jm_home_init_cnt: 0,
              oshical_home_init_cnt: 0,
              ad_sum: 0,
              jm_ad_click: 0,
              oshical_ad_click: 0,
            };

      // impressionSummaryをAPIデータから直接作成
      const impressionSummary = [
        {
          name: "雀活マスター",
          value: sumADInfo.jm_ad_cnt || 0,
          color: "#0088FE",
        },
        {
          name: "推しごとカレンダー",
          value: sumADInfo.oshical_ad_cnt || 0,
          color: "#00C49F",
        },
      ];

      // 実際の合計インプレッション数
      // const actualTotalImpressions =
      //   (sumADInfo.jm_ad_cnt || 0) + (sumADInfo.oshical_ad_cnt || 0);

      // const totalClicks = timeDistribution.reduce(
      //   (sum, item) => sum + item.impressions_jm,
      //   0,
      // );

      // 曜日別データ（実際のデータを使用）
      const weekDistribution =
        aDPerWeekInfoList.length > 0
          ? aDPerWeekInfoList.map((item) => ({
              day:
                item.week_day === "Monday"
                  ? "月"
                  : item.week_day === "Tuesday"
                    ? "火"
                    : item.week_day === "Wednesday"
                      ? "水"
                      : item.week_day === "Thursday"
                        ? "木"
                        : item.week_day === "Friday"
                          ? "金"
                          : item.week_day === "Saturday"
                            ? "土"
                            : item.week_day === "Sunday"
                              ? "日"
                              : "未知",
              impressions_jm: item.impressions_jm || 0,
              impressions_oshical: item.impressions_oshical || 0,
            }))
          : [
              { day: "月", impressions_jm: 180, impressions_oshical: 777 },
              { day: "火", impressions_jm: 200, impressions_oshical: 650 },
              { day: "水", impressions_jm: 150, impressions_oshical: 720 },
              { day: "木", impressions_jm: 220, impressions_oshical: 680 },
              { day: "金", impressions_jm: 250, impressions_oshical: 800 },
              { day: "土", impressions_jm: 180, impressions_oshical: 600 },
              { day: "日", impressions_jm: 120, impressions_oshical: 550 },
            ];
      return {
        impressionSummary,
        timeDistribution,
        weekDistribution,
        // clickData: {
        //   totalClicks,
        //   ctr:
        //     actualTotalImpressions > 0
        //       ? ((totalClicks / actualTotalImpressions) * 100).toFixed(2)
        //       : 0,
        // },
        periodInfo: {
          startDate: startDate.toLocaleDateString("ja-JP"),
          endDate: endDate.toLocaleDateString("ja-JP"),
          days: daysDiff,
        },
        sumADInfo,
        sumADInfoFrom,
        dayDistribution,
        dayGuestDistribution,
      };
    },
    [
      aDPerTimeInfoList,
      aDPerWeekInfoList,
      sumADInfoList,
      aDPerDayInfoList,
      guestPerDayInfoList,
      sumADInfoFromList,
    ],
  );

  // データをメモ化
  const mockData = useMemo(
    () => generateMockData(startDate, endDate),
    [generateMockData, startDate, endDate],
  );

  // totalImpressions を安全に計算
  const totalImpressions = useMemo(() => {
    try {
      if (
        !mockData?.impressionSummary ||
        !Array.isArray(mockData.impressionSummary)
      ) {
        return 0;
      }
      return mockData.impressionSummary.reduce((sum, item) => {
        const value = Number(item?.value) || 0;
        return sum + value;
      }, 0);
    } catch (error) {
      console.error("Error calculating totalImpressions:", error);
      return 0;
    }
  }, [mockData]);

  // ゲスト表現情報
  const guest_values = mockData.dayGuestDistribution.map((d) => d.guest_cnt);
  const guest_max = Math.max(...guest_values);
  const guest_min = Math.min(...guest_values);
  const guest_avg =
    guest_values.reduce((sum, v) => sum + v, 0) / guest_values.length;

  // 雀活マスターインプレッション数(日)
  const jm_day_imp_values = mockData.dayDistribution.map(
    (d) => d.impressions_jm,
  );
  const jm_day_imp_max = Math.max(...jm_day_imp_values);
  const jm_day_imp_min = Math.min(...jm_day_imp_values);
  const jm_day_imp_avg =
    jm_day_imp_values.reduce((sum, v) => sum + v, 0) / jm_day_imp_values.length;

  // 推しごとカレンダーインプレッション数(日)
  const oshical_day_imp_values = mockData.dayDistribution.map(
    (d) => d.impressions_oshical,
  );
  const oshical_day_imp_max = Math.max(...oshical_day_imp_values);
  const oshical_day_imp_min = Math.min(...oshical_day_imp_values);
  const oshical_day_imp_avg =
    oshical_day_imp_values.reduce((sum, v) => sum + v, 0) /
    oshical_day_imp_values.length;

  // 雀活マスターインプレッション数(時)
  const jm_time_imp_values = mockData.timeDistribution.map(
    (d) => d.impressions_jm,
  );
  const jm_time_imp_max = Math.max(...jm_time_imp_values);
  const jm_time_imp_min = Math.min(...jm_time_imp_values);
  const jm_time_imp_avg =
    jm_time_imp_values.reduce((sum, v) => sum + v, 0) /
    jm_time_imp_values.length;

  // 推しごとカレンダーインプレッション数(時)
  const oshical_time_imp_values = mockData.timeDistribution.map(
    (d) => d.impressions_oshical,
  );
  const oshical_time_imp_max = Math.max(...oshical_time_imp_values);
  const oshical_time_imp_min = Math.min(...oshical_time_imp_values);
  const oshical_time_imp_avg =
    oshical_time_imp_values.reduce((sum, v) => sum + v, 0) /
    oshical_time_imp_values.length;

  // 雀活マスターインプレッション数(曜)
  const jm_week_imp_values = mockData.weekDistribution.map(
    (d) => d.impressions_jm,
  );
  const jm_week_imp_max = Math.max(...jm_week_imp_values);
  const jm_week_imp_min = Math.min(...jm_week_imp_values);
  const jm_week_imp_avg =
    jm_week_imp_values.reduce((sum, v) => sum + v, 0) /
    jm_week_imp_values.length;

  // 推しごとカレンダーインプレッション数(曜)
  const oshical_week_imp_values = mockData.weekDistribution.map(
    (d) => d.impressions_oshical,
  );
  const oshical_week_imp_max = Math.max(...oshical_week_imp_values);
  const oshical_week_imp_min = Math.min(...oshical_week_imp_values);
  const oshical_week_imp_avg =
    oshical_week_imp_values.reduce((sum, v) => sum + v, 0) /
    oshical_week_imp_values.length;

  // 日付変更ハンドラー
  const handleStartDateChange = (date) => {
    if (date > endDate) {
      setEndDate(date);
    }
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    if (date < startDate) {
      setStartDate(date);
    }
    setEndDate(date);
  };

  // プリセット期間選択
  const selectPresetPeriod = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    setStartDate(start);
    setEndDate(end);
  };

  // プリセット期間選択
  const selectPresetPeriodFromTo = (fromdays, toDays) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - fromdays + 1);
    end.setDate(end.getDate() + toDays);
    setStartDate(start);
    setEndDate(end);
  };

  // ナビゲーションハンドラー
  const handleClickToHome = () => {
    navigate("/home");
  };

  const handleClickToLogout = () => {
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  // 期間計算
  const calculatePeriodDays = () => {
    return Math.ceil((endDate - startDate + 1) / (1000 * 60 * 60 * 24));
  };

  const peakData = mockData.timeDistribution.reduce(
    (max, current) =>
      current.impressions_jm > max.impressions_jm ? current : max,
    mockData.timeDistribution[0],
  );

  const togglePeriod = () => {
    setIsOpen((prev) => !prev);
  };

  // API呼び出し
  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const nextEndDate = new Date(endDate);
        nextEndDate.setDate(nextEndDate.getDate() + 1);

        const ad_banner_id = sessionStorage.getItem("ad_banner_id");

        const jADSearch = {
          bannerId: ad_banner_id,
          startDate: `${formatDate(startDate)} 00:00:00`,
          endDate: `${formatDate(nextEndDate)} 00:00:00`,
          seconds: 60,
        };

        // 時間単位のデータ取得
        const timeRes = await getADPerTimeInfoList(jADSearch);
        setADPerTimeInfoList(timeRes.data);

        // 日々単位のデータ取得(広告)
        const dayRes = await getADPerDayInfoList(jADSearch);
        setADPerDayInfoList(dayRes.data);
        // 日々単位のデータ取得(ゲスト)
        const dayGuestRes = await getGuestPerDayInfoList(jADSearch);
        setGuestPerDayInfoList(dayGuestRes.data);

        // 週単位のデータ取得
        const weekRes = await getADPerWeekInfoList(jADSearch);
        setADPerWeekInfoList(weekRes.data);

        // 広告集計情報
        const sumADRes = await getSumADInfo(jADSearch);
        setSumADInfoList(sumADRes.data);

        // 広告集計情報(指定期間以降の合計数字)
        const sumADFromRes = await getSumADInfoFrom(jADSearch);
        setSumADInfoFromList(sumADFromRes.data);
      } catch (err) {
        console.error("データ取得エラー:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, navigate]);

  return (
    <div className="dashboard-container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner" />
          <div className="loading-text">データを取得中...</div>
        </div>
      )}

      <header className="dashboard-header">
        <h1>
          広告パフォーマンスダッシュボード<br></br>
          {sessionStorage.getItem("ad_banner_name")} 様
        </h1>

        {/* 折りたたみボタン */}
        <button onClick={togglePeriod} className="preset-btn">
          {isOpen ? <FiChevronUp /> : <FiChevronDown />}
        </button>

        {/* 折りたたみコンテンツ */}
        {isOpen && (
          <div className="header-expandable">
            <div className="header-buttons">
              <button className="preset-btn" onClick={handleClickToHome}>
                ホーム
              </button>
              <button className="preset-btn" onClick={handleClickToLogout}>
                ログアウト
              </button>
            </div>
            <div className="period-controls">
              <div className="period-presets">
                <span className="preset-label">期間選択:</span>
                <button
                  className="preset-btn"
                  onClick={() => selectPresetPeriodFromTo(7, 7)}
                >
                  過去1週間～今後1週間
                </button>
                <button
                  className="preset-btn"
                  onClick={() => selectPresetPeriod(1)}
                >
                  今日
                </button>
                <button
                  className="preset-btn"
                  onClick={() => selectPresetPeriod(7)}
                >
                  過去7日間
                </button>
                <button
                  className="preset-btn"
                  onClick={() => selectPresetPeriod(30)}
                >
                  過去30日間
                </button>
                <button
                  className="preset-btn"
                  onClick={() => selectPresetPeriod(90)}
                >
                  過去90日間
                </button>
              </div>

              <div className="date-range-picker">
                <div className="date-input-group">
                  <label htmlFor="start-date">From:</label>
                  <div className="date-picker-wrapper">
                    <DatePicker
                      id="start-date"
                      selected={startDate}
                      onChange={handleStartDateChange}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={endDate}
                      dateFormat="yyyy/MM/dd"
                      className="date-picker-input"
                      popperPlacement="bottom-start"
                    />
                    <span className="calendar-icon">📅</span>
                  </div>
                </div>

                <div className="date-separator">〜</div>

                <div className="date-input-group">
                  <label htmlFor="end-date">To:</label>
                  <div className="date-picker-wrapper">
                    <DatePicker
                      id="end-date"
                      selected={endDate}
                      onChange={handleEndDateChange}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      dateFormat="yyyy/MM/dd"
                      className="date-picker-input"
                      popperPlacement="bottom-start"
                    />
                    <span className="calendar-icon">📅</span>
                  </div>
                </div>
                <div className="period-summary">
                  <span className="period-days">
                    {calculatePeriodDays()}日間
                  </span>
                  <span className="period-dates">
                    {startDate.toLocaleDateString("ja-JP")} 〜{" "}
                    {endDate.toLocaleDateString("ja-JP")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="tabs">
        <button
          className={activeTab === "jankatsu" ? "active" : ""}
          onClick={() => setActiveTab("jankatsu")}
        >
          総合概要
        </button>
        <button
          className={activeTab === "jankatsumaster" ? "active" : ""}
          onClick={() => setActiveTab("jankatsumaster")}
        >
          雀活マスター
        </button>
        <button
          className={activeTab === "oshical" ? "active" : ""}
          onClick={() => setActiveTab("oshical")}
        >
          推しごとカレンダー
        </button>
      </div>

      <div className="dashboard-content">
        <div className="sum-info-banner">
          <div className="period-info-content">
            <div className="period-title">2026年の累計値</div>
            <div className="period-details">
              <span className="period-item">
                <strong>インプレッション:</strong>{" "}
                {activeTab === "jankatsumaster"
                  ? (
                      mockData.sumADInfoFrom.jm_ad_cnt +
                      mockData.sumADInfoFrom.jm_home_init_cnt
                    ).toLocaleString()
                  : activeTab === "oshical"
                    ? (
                        mockData.sumADInfoFrom.oshical_ad_cnt +
                        mockData.sumADInfoFrom.oshical_home_init_cnt
                      ).toLocaleString()
                    : activeTab === "jankatsu"
                      ? (
                          mockData.sumADInfoFrom.jm_ad_cnt +
                          mockData.sumADInfoFrom.jm_home_init_cnt +
                          mockData.sumADInfoFrom.oshical_ad_cnt +
                          mockData.sumADInfoFrom.oshical_home_init_cnt
                        ).toLocaleString()
                      : ""}
                回
              </span>
              <span className="period-item">
                <strong>クリック:</strong>{" "}
                {activeTab === "jankatsumaster"
                  ? mockData.sumADInfoFrom.jm_ad_click.toLocaleString()
                  : activeTab === "oshical"
                    ? mockData.sumADInfoFrom.oshical_ad_click.toLocaleString()
                    : activeTab === "jankatsu"
                      ? (
                          mockData.sumADInfoFrom.jm_ad_click +
                          mockData.sumADInfoFrom.oshical_ad_click
                        ).toLocaleString()
                      : ""}
                回
              </span>
            </div>
          </div>
        </div>

        {/* 期間情報表示 */}
        <div className="period-info-banner">
          <div className="period-info-content">
            <div className="period-title">選択期間の分析結果</div>
            <div className="period-details">
              <span className="period-item">
                <strong>期間:</strong> {mockData.periodInfo.startDate} 〜{" "}
                {mockData.periodInfo.endDate}
              </span>
              <span className="period-item">
                <strong>日数:</strong> {mockData.periodInfo.days}日間
              </span>
              <span className="period-item">
                <strong>インプレッション:</strong>{" "}
                {activeTab === "jankatsumaster"
                  ? mockData.sumADInfo.jm_ad_cnt.toLocaleString()
                  : activeTab === "oshical"
                    ? mockData.sumADInfo.oshical_ad_cnt.toLocaleString()
                    : activeTab === "jankatsu"
                      ? totalImpressions.toLocaleString()
                      : ""}
                回
              </span>
              <span className="period-item">
                <strong>クリック:</strong>{" "}
                {activeTab === "jankatsumaster"
                  ? mockData.sumADInfo.jm_ad_click.toLocaleString()
                  : activeTab === "oshical"
                    ? mockData.sumADInfo.oshical_ad_click.toLocaleString()
                    : activeTab === "jankatsu"
                      ? (
                          mockData.sumADInfo.jm_ad_click +
                          mockData.sumADInfo.oshical_ad_click
                        ).toLocaleString()
                      : ""}
                回
              </span>
            </div>
          </div>
        </div>

        {/* ===== Impression Analysis ===== */}
        {/* ① インプレッション数サマリー */}
        <div className="chart-section">
          <h2>
            ■インプレッション内訳(
            {activeTab === "jankatsumaster"
              ? "雀活マスター"
              : activeTab === "jankatsu"
                ? "雀活マスター ➕ 推しごとカレンダー"
                : "推しごとカレンダー"}
            )
          </h2>
          <div className="chart-row">
            {activeTab === "jankatsu" ? (
              <div className="chart-container pie-chart">
                <h3>①インプレッション数分布</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={mockData.impressionSummary}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={90}
                      label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                    >
                      {mockData.impressionSummary.map((_, i) => (
                        <Cell key={i} fill={COLORS[i]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip formatter={(v) => `${v.toLocaleString()} 回`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="chart-summary">
                  <div className="summary-item">
                    <span className="label">期間内合計インプレッション数</span>
                    <span className="value">
                      {totalImpressions.toLocaleString()} 回
                    </span>
                  </div>
                  {mockData.impressionSummary.map((item, index) => {
                    const percentage =
                      totalImpressions > 0
                        ? (
                            (Number(item.value) / totalImpressions) *
                            100
                          ).toFixed(1)
                        : "0.0";
                    const itemValue = Number(item.value) || 0;

                    return (
                      <div className="summary-item" key={index}>
                        <span className="label">{item.name}</span>
                        <span className="value">
                          {itemValue.toLocaleString()} 回 ({percentage}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <></>
            )}

            {/* ② 時間分布チャート */}
            <div className="chart-container">
              {/* ===== 上部ヘッダー ===== */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  marginBottom: 8,
                  gap: 8,
                }}
              >
                {/* ===== 左：雀活マスター ===== */}
                <div>
                  {(activeTab === "jankatsumaster" ||
                    activeTab === "jankatsu") && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        // padding: "6px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ color: JM_COLOR.max }}>
                        {JM_MOJI.max}：{jm_time_imp_max}
                      </div>
                      <div style={{ color: JM_COLOR.avg }}>
                        {JM_MOJI.avg}：{jm_time_imp_avg.toFixed(0)}
                      </div>
                      <div style={{ color: JM_COLOR.min }}>
                        {JM_MOJI.min}：{jm_time_imp_min}
                      </div>
                    </div>
                  )}
                </div>

                {/* ===== 中央：タイトル ===== */}
                <h3
                  style={{
                    margin: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeTab === "jankatsu" ? "②" : "①"}
                  時間帯別インプレッション分布
                </h3>

                {/* ===== 右：推しごとカレンダー ===== */}
                <div>
                  {(activeTab === "oshical" || activeTab === "jankatsu") && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        padding: "6px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                        textAlign: "right",
                      }}
                    >
                      <div style={{ color: OSHICAL_COLOR.max }}>
                        {OSHICAL_MOJI.max}：{oshical_time_imp_max}
                      </div>
                      <div style={{ color: OSHICAL_COLOR.avg }}>
                        {OSHICAL_MOJI.avg}：{oshical_time_imp_avg.toFixed(0)}
                      </div>
                      <div style={{ color: OSHICAL_COLOR.min }}>
                        {OSHICAL_MOJI.min}：{oshical_time_imp_min}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div></div>

              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={mockData.timeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "impressions_jm")
                        return [
                          `${value.toLocaleString()} 回`,
                          "インプレッション数(雀)",
                        ];
                      if (name === "impressions_oshical")
                        return [`${value} 回`, "インプレッション数(推)"];
                      return value;
                    }}
                  />
                  <Legend />
                  {/* ===== 雀活マスター ===== */}
                  {(activeTab === "jankatsumaster" ||
                    activeTab === "jankatsu") && (
                    <>
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_time_imp_max}
                        stroke={JM_COLOR.max}
                        strokeDasharray="4 4"
                        label={
                          showRefLabel
                            ? {
                                value: "最大",
                                position: "right",
                                fill: JM_COLOR.max,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_time_imp_avg}
                        stroke={JM_COLOR.avg}
                        strokeDasharray="4 4"
                        label={
                          showRefLabel
                            ? {
                                value: "平均",
                                position: "right",
                                fill: JM_COLOR.avg,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_time_imp_min}
                        stroke={JM_COLOR.min}
                        strokeDasharray="4 4"
                        label={
                          showRefLabel
                            ? {
                                value: "最小",
                                position: "right",
                                fill: JM_COLOR.min,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />

                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="impressions_jm"
                        name="雀活マスター"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                      />
                    </>
                  )}

                  {/* ===== 推しごとカレンダー ===== */}
                  {(activeTab === "oshical" || activeTab === "jankatsu") && (
                    <>
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_time_imp_max}
                        stroke={OSHICAL_COLOR.max}
                        strokeDasharray="2 2"
                        label={
                          showRefLabel
                            ? {
                                value: "最大",
                                position: "left",
                                fill: OSHICAL_COLOR.max,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_time_imp_avg}
                        stroke={OSHICAL_COLOR.avg}
                        strokeDasharray="2 2"
                        label={
                          showRefLabel
                            ? {
                                value: "平均",
                                position: "left",
                                fill: OSHICAL_COLOR.avg,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_time_imp_min}
                        stroke={OSHICAL_COLOR.min}
                        strokeDasharray="2 2"
                        label={
                          showRefLabel
                            ? {
                                value: "最小",
                                position: "left",
                                fill: OSHICAL_COLOR.min,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="impressions_oshical"
                        name="推しごとカレンダー"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                      />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/*  曜日別インプレッション推移 */}
            <div className="chart-container">
              {/* ===== 上部ヘッダー ===== */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  marginBottom: 8,
                  gap: 8,
                }}
              >
                {/* ===== 左：雀活マスター ===== */}
                <div>
                  {(activeTab === "jankatsumaster" ||
                    activeTab === "jankatsu") && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        padding: "6px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ color: JM_COLOR.max }}>
                        {JM_MOJI.max}：{jm_week_imp_max}
                      </div>
                      <div style={{ color: JM_COLOR.avg }}>
                        {JM_MOJI.avg}：{jm_week_imp_avg.toFixed(0)}
                      </div>
                      <div style={{ color: JM_COLOR.min }}>
                        {JM_MOJI.min}：{jm_week_imp_min}
                      </div>
                    </div>
                  )}
                </div>

                {/* ===== 中央：タイトル ===== */}
                <h3
                  style={{
                    margin: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeTab === "jankatsu" ? "③" : "②"}
                  曜日別インプレッション推移
                </h3>

                {/* ===== 右：推しごとカレンダー ===== */}
                <div>
                  {(activeTab === "oshical" || activeTab === "jankatsu") && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        padding: "6px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                        textAlign: "right",
                      }}
                    >
                      <div style={{ color: OSHICAL_COLOR.max }}>
                        {OSHICAL_MOJI.max}：{oshical_week_imp_max}
                      </div>
                      <div style={{ color: OSHICAL_COLOR.avg }}>
                        {OSHICAL_MOJI.avg}：{oshical_week_imp_avg.toFixed(0)}
                      </div>
                      <div style={{ color: OSHICAL_COLOR.min }}>
                        {OSHICAL_MOJI.min}：{oshical_week_imp_min}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div></div>

              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={mockData.weekDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />

                  {/* 左右の軸を定義 */}
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />

                  <Tooltip
                    formatter={(value, name) => [
                      `${value} 回`,
                      "インプレッション数",
                    ]}
                    labelFormatter={(label) => `${label}曜日`}
                  />
                  <Legend />

                  {/* ===== 雀活マスター ===== */}
                  {(activeTab === "jankatsumaster" ||
                    activeTab === "jankatsu") && (
                    <>
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_week_imp_max}
                        stroke={JM_COLOR.max}
                        strokeDasharray="4 1" // 細かめの破線で右の線と差別化
                        strokeWidth={2}
                        label={
                          showRefLabel
                            ? {
                                value: "最大",
                                position: "right",
                                fill: JM_COLOR.max,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_week_imp_avg}
                        stroke={JM_COLOR.avg}
                        strokeDasharray="2 2" // 中間の線は中破線
                        strokeWidth={2}
                        label={
                          showRefLabel
                            ? {
                                value: "平均",
                                position: "right",
                                fill: JM_COLOR.avg,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_week_imp_min}
                        stroke={JM_COLOR.min}
                        strokeDasharray="1 1" // 細い点線で最小を表現
                        strokeWidth={2}
                        label={
                          showRefLabel
                            ? {
                                value: "最小",
                                position: "right",
                                fill: JM_COLOR.min,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="impressions_jm"
                        name="雀活マスター"
                        fill="#0088FE"
                        radius={[3, 3, 0, 0]}
                      />
                    </>
                  )}

                  {/* ===== 推しごとカレンダー ===== */}
                  {(activeTab === "oshical" || activeTab === "jankatsu") && (
                    <>
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_week_imp_max}
                        stroke={OSHICAL_COLOR.max}
                        strokeDasharray="5 5" // 左とは違う破線パターン
                        strokeWidth={2}
                        label={
                          showRefLabel
                            ? {
                                value: "最大",
                                position: "left",
                                fill: OSHICAL_COLOR.max,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_week_imp_avg}
                        stroke={OSHICAL_COLOR.avg}
                        strokeDasharray="3 3"
                        strokeWidth={2}
                        label={
                          showRefLabel
                            ? {
                                value: "平均",
                                position: "left",
                                fill: OSHICAL_COLOR.avg,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_week_imp_min}
                        stroke={OSHICAL_COLOR.min}
                        strokeDasharray="1 3"
                        strokeWidth={2}
                        label={
                          showRefLabel
                            ? {
                                value: "最小",
                                position: "left",
                                fill: OSHICAL_COLOR.min,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="impressions_oshical"
                        name="推しごとカレンダー"
                        fill="#82ca9d"
                        radius={[3, 3, 0, 0]}
                      />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* ④ 日々別インプレッション分布チャート */}
            <div className="chart-container">
              {/* ===== 上部ヘッダー ===== */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  marginBottom: 8,
                  gap: 8,
                }}
              >
                {/* ===== 左：雀活マスター ===== */}
                <div>
                  {(activeTab === "jankatsumaster" ||
                    activeTab === "jankatsu") && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        padding: "6px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ color: JM_COLOR.max }}>
                        {JM_MOJI.max}：{jm_day_imp_max}
                      </div>
                      <div style={{ color: JM_COLOR.avg }}>
                        {JM_MOJI.avg}：{jm_day_imp_avg.toFixed(0)}
                      </div>
                      <div style={{ color: JM_COLOR.min }}>
                        {JM_MOJI.min}：{jm_day_imp_min}
                      </div>
                    </div>
                  )}
                </div>

                {/* ===== 中央：タイトル ===== */}
                <h3
                  style={{
                    margin: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeTab === "jankatsu" ? "④" : "③"}
                  日々別インプレッション分布
                </h3>

                {/* ===== 右：推しごとカレンダー ===== */}
                <div>
                  {(activeTab === "oshical" || activeTab === "jankatsu") && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.85)",
                        padding: "6px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                        textAlign: "right",
                      }}
                    >
                      <div style={{ color: OSHICAL_COLOR.max }}>
                        {OSHICAL_MOJI.max}：{oshical_day_imp_max}
                      </div>
                      <div style={{ color: OSHICAL_COLOR.avg }}>
                        {OSHICAL_MOJI.avg}：{oshical_day_imp_avg.toFixed(0)}
                      </div>
                      <div style={{ color: OSHICAL_COLOR.min }}>
                        {OSHICAL_MOJI.min}：{oshical_day_imp_min}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div></div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={mockData.dayDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="target_date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />

                  {/* ===== 雀活マスター ===== */}
                  {(activeTab === "jankatsumaster" ||
                    activeTab === "jankatsu") && (
                    <>
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_day_imp_max}
                        stroke={JM_COLOR.max}
                        strokeDasharray="4 4"
                        label={
                          showRefLabel
                            ? {
                                value: "最大",
                                position: "right",
                                fill: JM_COLOR.max,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_day_imp_avg}
                        stroke={JM_COLOR.avg}
                        strokeDasharray="4 4"
                        label={
                          showRefLabel
                            ? {
                                value: "平均",
                                position: "right",
                                fill: JM_COLOR.avg,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={jm_day_imp_min}
                        stroke={JM_COLOR.min}
                        strokeDasharray="4 4"
                        label={
                          showRefLabel
                            ? {
                                value: "最小",
                                position: "right",
                                fill: JM_COLOR.min,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />

                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="impressions_jm"
                        name="雀活マスター"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </>
                  )}

                  {/* ===== 推しごとカレンダー ===== */}
                  {(activeTab === "oshical" || activeTab === "jankatsu") && (
                    <>
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_day_imp_max}
                        stroke={OSHICAL_COLOR.max}
                        strokeDasharray="2 2"
                        label={
                          showRefLabel
                            ? {
                                value: "最大",
                                position: "left",
                                fill: OSHICAL_COLOR.max,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_day_imp_avg}
                        stroke={OSHICAL_COLOR.avg}
                        strokeDasharray="2 2"
                        label={
                          showRefLabel
                            ? {
                                value: "平均",
                                position: "left",
                                fill: OSHICAL_COLOR.avg,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />
                      <ReferenceLine
                        yAxisId="right"
                        y={oshical_day_imp_min}
                        stroke={OSHICAL_COLOR.min}
                        strokeDasharray="2 2"
                        label={
                          showRefLabel
                            ? {
                                value: "最小",
                                position: "left",
                                fill: OSHICAL_COLOR.min,
                                fontSize: 12,
                              }
                            : undefined
                        }
                      />

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="impressions_oshical"
                        name="推しごとカレンダー"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* ⑤ 日々別ゲスト分布チャート */}
            {activeTab === "oshical" || activeTab === "jankatsu" ? (
              <div className="chart-container">
                {/* ===== 上部ヘッダー ===== */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  {/* ===== 左：雀活マスター ===== */}
                  <div>
                    {(activeTab === "oshical" || activeTab === "jankatsu") && (
                      <div
                        style={{
                          background: "rgba(255,255,255,0.85)",
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: 12,
                        }}
                      >
                        <div style={{ color: OSHICAL_COLOR.max }}>
                          {OSHICAL_MOJI.max}：{guest_max}
                        </div>
                        <div style={{ color: JM_COLOR.avg }}>
                          {OSHICAL_MOJI.avg}：{guest_avg.toFixed(0)}
                        </div>
                        <div style={{ color: JM_COLOR.min }}>
                          {OSHICAL_MOJI.min}：{guest_min}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ===== 中央：タイトル ===== */}
                  <h3
                    style={{
                      margin: 0,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activeTab === "jankatsu" ? "④" : "③"}
                    日々別ゲスト分布
                  </h3>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={mockData.dayGuestDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="target_date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />

                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "guest_cnt")
                          return [`${value} 人`, "ゲスト数"];
                        return value;
                      }}
                    />
                    <Legend />

                    {/* 最大 */}
                    <ReferenceLine
                      yAxisId="left"
                      y={guest_max}
                      stroke={OSHICAL_COLOR.max}
                      strokeDasharray="4 4"
                      label={{
                        value: "最大",
                        position: "right",
                        fill: OSHICAL_COLOR.max,
                        fontSize: 12,
                      }}
                    />

                    {/* 最小 */}
                    <ReferenceLine
                      yAxisId="left"
                      y={guest_min}
                      stroke={OSHICAL_COLOR.min}
                      strokeDasharray="4 4"
                      label={{
                        value: "最小",
                        position: "right",
                        fill: OSHICAL_COLOR.min,
                        fontSize: 12,
                      }}
                    />

                    {/* 平均 */}
                    <ReferenceLine
                      yAxisId="left"
                      y={guest_avg}
                      stroke={OSHICAL_COLOR.avg}
                      strokeDasharray="4 4"
                      label={{
                        value: "平均",
                        position: "right",
                        fill: OSHICAL_COLOR.avg,
                        fontSize: 12,
                      }}
                    />

                    {/* 既存 Line（そのまま） */}
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="guest_cnt"
                      name="ゲスト"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : null}
          </div>
        </div>

        {/* ③ クリック数分析 */}
        {activeTab === "jankatsumaster" || activeTab === "jankatsu" ? (
          <div className="chart-section">
            <h2>雀活マスターアプリ</h2>
            <div className="chart-row">
              <div className="kpi-cards">
                <div className="kpi-card">
                  <div className="kpi-icon">📊</div>
                  <div className="kpi-content">
                    <div className="kpi-label">ユニークユーザー数</div>
                    <div className="kpi-value">
                      {mockData.sumADInfo.user_cnt.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-icon">🎯</div>
                  <div className="kpi-content">
                    <div className="kpi-label">起動回数</div>
                    <div className="kpi-value">
                      {mockData.sumADInfo.jm_home_init_cnt.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-icon">📈</div>
                  <div className="kpi-content">
                    <div className="kpi-label">平均起動回数</div>
                    <div className="kpi-value">
                      {mockData.sumADInfo.user_cnt > 0
                        ? Math.round(
                            mockData.sumADInfo.jm_home_init_cnt /
                              mockData.sumADInfo.user_cnt,
                          ).toLocaleString()
                        : "0"}
                    </div>
                    <div className="kpi-unit">回/人</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* 総合概要 */}
        {activeTab === "jankatsu" ? (
          <div className="overview-section">
            <h2>総合パフォーマンス</h2>
            <div className="performance-grid">
              <div className="performance-card">
                <div className="performance-icon">📈</div>
                <div className="performance-content">
                  <h3>インプレッション効率</h3>
                  <div className="performance-metric">
                    <span className="metric-value">
                      {mockData.sumADInfo.ad_sum > 0
                        ? (
                            ((mockData.sumADInfo.jm_ad_cnt +
                              mockData.sumADInfo.oshical_ad_cnt) /
                              mockData.sumADInfo.ad_sum) *
                            100
                          ).toFixed(1)
                        : "0.0"}
                      %
                    </span>
                    <span className="metric-label">インプレッション率</span>
                  </div>
                </div>
              </div>

              <div className="performance-card">
                <div className="performance-icon">⏰</div>
                <div className="performance-content">
                  <h3>ピーク時間帯</h3>
                  <div className="performance-metric">
                    <span className="metric-value">{peakData.time}</span>
                    <span className="metric-label">
                      {peakData.impressions_jm.toLocaleString()}{" "}
                      インプレッション
                    </span>
                  </div>
                  <div className="performance-note">
                    最も効果的な広告表示時間
                  </div>
                </div>
              </div>
              {/* <div className="performance-card">
                <div className="performance-icon">🎪</div>
                <div className="performance-content">
                  <h3>雀活マスター効果</h3>
                  <div className="performance-metric">
                    <span className="metric-value">
                      {totalImpressions > 0
                        ? (
                            (mockData.sumADInfo.jm_ad_cnt / totalImpressions) *
                            100
                          ).toFixed(1)
                        : "0.0"}
                      %
                    </span>
                    <span className="metric-label">全体表示数に占める割合</span>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Dashboard;
